import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";

const API = "https://api.vyapakprint.com/api";

const formatCurrency = (val) =>
  Number(val || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleString("en-IN");
};

const ClientLedger = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [query, setQuery] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState("");

  // ✅ Fetch user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await axios.get(`${API}/distribution/${id}`);

        const data =
          res?.data?.data ||
          res?.data?.distribution ||
          res?.data ||
          null;

        setUser(data);
      } catch (err) {
        console.error(err);
        setError("Could not load client details.");
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id]);

  const transactions = user?.transactionHistory || [];

  // ✅ Filter
  const filteredTxns = useMemo(() => {
    const q = query.toLowerCase();
    if (!q) return transactions;

    return transactions.filter((t) => {
      return (
        String(t?.amount || "").includes(q) ||
        String(t?.remark || "").toLowerCase().includes(q) ||
        String(t?.receiptId || "").toLowerCase().includes(q) ||
        String(t?.transactionType || "").toLowerCase().includes(q)
      );
    });
  }, [transactions, query]);

  // ✅ Edit
  const startEdit = (txn) => {
    setEditingId(txn._id);
    setEditingValue(txn.remark || "");
    setSuccess("");
    setError("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingValue("");
  };

  const saveEdit = async (txn) => {
    const newRemark = editingValue.trim();

    if (newRemark === (txn.remark || "")) {
      cancelEdit();
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      await axios.put(
        `${API}/distribution/${id}/transaction/${txn._id}`,
        { remark: newRemark }
      );

      // ✅ Update UI
      setUser((prev) => ({
        ...prev,
        transactionHistory: prev.transactionHistory.map((t) =>
          t._id === txn._id ? { ...t, remark: newRemark } : t
        ),
      }));

      setSuccess("Remark updated successfully");
      cancelEdit();
    } catch (err) {
      console.error(err);
      setError("Failed to update remark.");
    } finally {
      setSaving(false);
    }
  };

  // ✅ Export
  const exportToExcel = () => {
    const rows = filteredTxns.map((t) => ({
      Date: formatDate(t?.date),
      Type: t?.transactionType || "-",
      Amount: t?.amount || 0,
      PreviousBalance: t?.previousBalance || 0,
      NewBalance: t?.newBalance || 0,
      Remark: t?.remark || "-",
      ReceiptID: t?.receiptId || "-",
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ledger");

    XLSX.writeFile(wb, `${user?.contactPerson || "ledger"}.xlsx`);
  };

  if (loading) return <div className="p-6">Loading...</div>;

  if (error) {
    return <div className="p-6 text-red-600">{error}</div>;
  }

  if (!user) {
    return <div className="p-6">User not found</div>;
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* BACK */}
      <button
        onClick={() => navigate(-1)}
        className="mb-4 text-blue-600 hover:underline"
      >
        ← Back
      </button>

      {/* HEADER */}
      <h2 className="mb-3 text-2xl font-semibold">
        Ledger –{" "}
        <span className="text-base text-gray-600">
          {user?.contactPerson} ({user?.businessName})
        </span>
      </h2>

      {/* ALERTS */}
      {error && <div className="mb-3 text-red-600">{error}</div>}
      {success && <div className="mb-3 text-green-600">{success}</div>}

      {/* TOP BAR */}
      <div className="mb-3 flex justify-between">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search..."
          className="p-2 border rounded w-52"
        />

        <button
          onClick={exportToExcel}
          className="bg-green-600 text-white px-3 py-2 rounded"
        >
          Export
        </button>
      </div>

      {/* TABLE */}
      <div className="bg-white rounded shadow overflow-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2 text-left">Date</th>
              <th className="p-2 text-left">Type</th>
              <th className="p-2 text-right">Amount</th>
              <th className="p-2 text-right">Prev</th>
              <th className="p-2 text-right">New</th>
              <th className="p-2 text-left">Remark</th>
              <th className="p-2 text-left">Receipt</th>
              <th className="p-2 text-center">Action</th>
            </tr>
          </thead>

          <tbody>
            {filteredTxns.length ? (
              filteredTxns.map((t) => (
                <tr key={t._id} className="border-t">
                  <td className="p-2">{formatDate(t?.date)}</td>

                  <td className="p-2 capitalize">
                    {t?.transactionType || "-"}
                  </td>

                  <td className="p-2 text-right font-semibold">
                    ₹{formatCurrency(t?.amount)}
                  </td>

                  <td className="p-2 text-right">
                    ₹{formatCurrency(t?.previousBalance)}
                  </td>

                  <td className="p-2 text-right">
                    ₹{formatCurrency(t?.newBalance)}
                  </td>

                  <td className="p-2">
                    {editingId === t._id ? (
                      <input
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        className="border p-1 rounded w-full"
                      />
                    ) : (
                      t?.remark || "-"
                    )}
                  </td>

                  <td className="p-2">{t?.receiptId || "-"}</td>

                  <td className="p-2 text-center">
                    {editingId === t._id ? (
                      <>
                        <button
                          onClick={() => saveEdit(t)}
                          disabled={saving}
                          className="mr-2 text-green-600"
                        >
                          Save
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="text-gray-600"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => startEdit(t)}
                        className="text-blue-600"
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center p-4 text-gray-500">
                  No transactions found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ClientLedger;